import { Component, ViewChild, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { NgbModal, ModalDismissReasons } from '@ng-bootstrap/ng-bootstrap';
import { NgbTabChangeEvent } from '@ng-bootstrap/ng-bootstrap';
import { Observable } from 'rxjs/Observable';
import { AuthService } from '../Services/auth';
import { UserService } from '../Services/user';
import { ProductService } from '../Services/product';
import { AlertService } from '../Services/alert';
import { MessageService } from '../Services/message';
import { Config } from '../Services/config';
import { ImageUtils } from '../Services/imageutils';
import { LanguageService } from '../Services/lang';
import { User } from '../Models/user';
import { Address } from '../Models/address';
import { Product } from '../Models/product';
import { Order } from '../Models/order';
import { Message } from '../Models/message';
import { Language } from '../Models/language';
import { Page } from '../Models/page';
import { ProductFilter } from '../Models/filter';


@Component({
  selector: 'app-profile',
  templateUrl: '../Templates/profile.html',
  styleUrls: ['../Styles/profile.scss']
})


export class ProfileComponent {

  user: User;
  wishlist: Product[];
  following: User[];
  products: Page<Product>;
  messages: Message[];
  settings: Map<string, any>;
  orders: Order[];
  languages: Language[];
  filter: ProductFilter;
  lang_code: string;

  // Form submitted?
  submitted = false;

  @ViewChild('tabs') tabs;

  constructor(private cd: ChangeDetectorRef,
              private route: ActivatedRoute,
              private location: Location,
              private auth: AuthService,
              private productService: ProductService,
              private alerts: AlertService,
              private config: Config,
              private msg: MessageService,
              private lang: LanguageService,
              private imageUtils: ImageUtils,
              private userService: UserService,
              private modalService: NgbModal) { }

  ngOnInit() {

    this.filter = new ProductFilter();

    this.auth.getUser().subscribe(
      user => {
        this.user = user;

        if (this.user == null) {
          return;
        }

        if (this.user._links.wishlist) {
          this.userService.getWishlist(this.user).subscribe(
            data => { this.wishlist = data }
          );
        }

        if (this.user._links.messages) {
          this.msg.list(this.user).subscribe(
            data => { this.messages = data['results'] }
          );
        }

        if (this.user._links.following) {
          this.userService.getFollowing(this.user).subscribe(
            data => { this.following = data }
          );
        }

        if (this.user._links.products) {
          this.userService.getProducts(this.user).subscribe(
            data => { this.products = data }
          );
        }

        if (this.user._links.orders) {
          this.userService.getOrders(this.user).subscribe(
            data => { this.orders = data }
          );
        }

      }
    );

    this.config.getSettings().subscribe(
      data => { this.settings = data; }
    );

    this.productService.productsChanged$.subscribe(
      data => {
        this.userService.getProducts(this.user).subscribe(
          data => { this.products = data }
        );
      }
    )

    this.userService.wishlistChanged$.subscribe(
      data => {
        this.userService.getWishlist(this.user).subscribe(
          data => { this.wishlist = data }
        );
      }
    )

    this.userService.watchChanged$.subscribe(
      data => {
        this.userService.getFollowing(this.user).subscribe(
          data => { this.following = data }
        );
      }
    )

    this.lang.list().subscribe(
      data => { this.languages = data }
    );

    this.lang.getLanguage().subscribe(
      data => { this.lang_code = data.id }
    );

  }

  ngAfterViewInit() {

    this.route.fragment.subscribe(fragment => {
      this.tabs.select(fragment);
    });

    this.route.params.subscribe(params => {
      this.tabs.select(params['frag']);
    });

    this.cd.detectChanges();
  }

  /**
   * Keep URL of tab, for navigation purposes.
   */
  public tabChange($event: NgbTabChangeEvent) {
    this.location.go(`/profile/${$event.nextId}`);
  }

  saveProfile(): void {

    this.userService.update(this.user).subscribe(
      user => {
        this.user = user;
        this.alerts.success("Profile updated");
      },
      err => { this.alerts.error(err) })
  }

  toggleEmailNotification() {
    if (this.user.subscription_channel.indexOf('email') > -1) {
      this.user.subscription_channel.splice(
        this.user.subscription_channel.indexOf('email'), 1);
    } else {
      this.user.subscription_channel.push('email');
    }

    this.saveProfile();
  }

  unFollow(product, idx): void {

    this.productService.unFollow(product).subscribe(
      product => {
        this.userService.unfollowing(product);
        this.alerts.success("Product removed from wishlist")
        this.wishlist.splice(idx, 1);
      },
      err => {
        if (err.name == "TimeoutError") {
          this.alerts.error("Your connection is slow! Please be patient");
        } else {
          this.alerts.error("Could not remove from wishlist")
        }
      }
    );
  }

  unFollowUser(user, idx): void {

    this.userService.unFollow(user).subscribe(
      user => {
        this.alerts.success("User removed from list")
        this.following.splice(idx, 1);
      },
      err => {
        this.alerts.error("Could not remove from list")
      }
    );
  }

  public removeMessage(message: Message, idx: number): void {

    this.msg.remove(message).subscribe(
      data => {
        this.messages.splice(idx, 1);
      }
    );
  }

  public markMessage(message: Message, idx: number, flag: string): void {

    if (flag == 'seen') {
      this.msg.markSeen(message).subscribe(
        data => {
          this.messages[idx] = data;
        }
      );
    } else {
      this.msg.markUnseen(message).subscribe(
        data => {
          this.messages[idx] = data;
        }
      );
    }
  }

  public filterList(reset?: boolean): void {

    if (reset) {
      this.filter.clear('page');
    }

    this.userService.getProducts(this.user, this.filter).subscribe(
      data => { this.products = data }
    );
  }

  public getWFStateClass(state: number): string {

    let map = {
      1: 'label-success',
      0: 'label-info',
      3: 'label-danger',
      2: 'label-warning'
    }

    return map[state];
  }

  public getWFState(state: number): Observable<string> {

    let map = {
      0: this.lang.translate("Awaiting acceptance",false),
      1: this.lang.translate("Accepted",false),
      2: this.lang.translate("Accepted",false ), // We don't show 'reserved'...
      3: this.lang.translate("Sold",false),
      4: this.lang.translate("Shipped", false )
    }

    return map[state];
  }

  getExpiryDate(product) {

    let today = new Date();

    today.setDate(today.getDate() + product.ttl);

    return today;
  }

  canRemove(product: Product): boolean {

    return (product.wfstate < 3);
  }

  removeProduct(product, confirm) {

    this.modalService.open(confirm).result.then((result) => {
      if (result == "y") {
        this.productService.remove(product).subscribe(
          data => {}
        );
      }
    });
  }

  /**
   * Give the product a kick, so it's alive for some more time...
   */
  reviveProduct(product: Product, idx: number) {
    this.productService.revive(product).subscribe(
      data => {
        this.products[idx] = data;
        this.alerts.success("Product revived")
      }
    );
  }

  /**
   * Revive all products that are due for revival. The construction is
   * a tad awkward, given that the callback needs local variables,
   * cause the loop changes globals before the callback is executed.
   */
  public reviveProducts(): void {

    let productsRef = this.products;

    if (!this.products) {
      return;
    }

    for (var i = 0; i < this.products.length; i++) {

      if (this.products[i].canRevive()) {

        let j = i;
        let callback = function(data) {
          productsRef[j] = data;
        }

        this.productService.revive(this.products[i]).subscribe(callback);
      }
    }
  }

  isShipped(product: Product): boolean {

    return product.wfstate == 4;
  }

  markShipped(product: Product, idx: number) {

    this.productService.markShipped(product).subscribe(
      data => {
        this.products[idx] = data;
        this.alerts.success("Product has been marked as shipped")
      }
    );
  }

  saveAddress(address: Address): void {

    for (var key in address) {

      if (typeof key == typeof "") {
        this.user[key] = address[key];
      }
    }
  }

  /**
   * Handle paging
   */
  public handlePageChange(params: string) {

    this.filter.setValuesFromURL("?" + params);

    this.filterList();
  }

}



// WEBPACK FOOTER //
// ./src/app/Components/profile.ts